import { WorkflowReviewRequestForm } from '@app/generated/contracts/workflow-review-request.generated';
import { WorkflowReviewSubmissionRequestForm } from '@app/generated/contracts/workflow-review-submission-request.generated';
import { WorkflowReviewRecordRequestForm } from '@app/generated/contracts/workflow-review-record-request.generated';
import type { FormIssues } from '@template-ui/helpers/generated-form.helpers';
import { editableFormPayload, validateGeneratedForm } from '@template-ui/helpers/generated-form.helpers';
import type { WorkflowClientCommandAdapter, WorkflowClientCommandModule } from './workflow-command-registry';
import { providedLookupRevisions, resolveProvidedLookupFields, workflowFormLookupBindingKey } from './workflow-form-lookup';

const occurrenceBinding: WorkflowClientCommandAdapter['bindContext'] = context => ({
  occurrenceId: context.occurrenceId,
  expectedETag: context.stepETag,
});

const preparation: WorkflowClientCommandAdapter = {
  key: { provider: 'workflow', kind: 'command', code: 'workflow.preparation.submit', version: 1 },
  form: WorkflowReviewRequestForm,
  endpointSuffix: '/preparation',
  submitLabel: 'Submit work for review',
  submissionHint: 'Saves your work and opens a fresh independent review.',
  confirmation: 'Work submitted for review.',
  bindContext: occurrenceBinding,
};
const requestReview: WorkflowClientCommandAdapter = {
  key: { provider: 'workflow', kind: 'command', code: 'workflow.review.request', version: 1 },
  form: WorkflowReviewRequestForm,
  endpointSuffix: '/review/request',
  submitLabel: 'Send review request',
  submissionHint: 'Sends a review request for this visit.',
  confirmation: 'Review request saved.',
  bindContext: occurrenceBinding,
};
const submitReview: WorkflowClientCommandAdapter = {
  key: { provider: 'workflow', kind: 'command', code: 'workflow.review.submit', version: 1 },
  form: WorkflowReviewSubmissionRequestForm,
  endpointSuffix: '/review',
  submitLabel: 'Save review and continue',
  submissionHint: 'Saves your decision and applies the configured next step.',
  confirmation: 'Review saved.',
  outcomeField: 'outcome',
  outcomePrompt: 'Choose a review outcome to continue.',
  bindContext: (context, action) => Number.isSafeInteger(action.decisionRecordId) && action.decisionRecordId! > 0
    ? { ...occurrenceBinding(context, action), decisionRecordId: action.decisionRecordId } : null,
  formForAction: action => ({
    ...WorkflowReviewSubmissionRequestForm,
    fields: WorkflowReviewSubmissionRequestForm.fields.map(field => field.name === 'outcome'
      ? { ...field, options: action.outcomes.map(outcome => ({ value: outcome.code, label: outcome.label, labelToken: '' })) }
      : field),
  }),
  validateInput: (action, values): FormIssues => action.outcomes.find(outcome => outcome.code === values['outcome'])?.reasonPolicy === 'required'
    && !String(values['notes'] ?? '').trim() ? { notes: 'A reason is required for this outcome.' } : {},
};

export const workflowReviewCommandModule: WorkflowClientCommandModule = {
  code: 'workflow', version: 1, commands: [preparation, requestReview, submitReview, {
    ...submitReview,
    key: { ...submitReview.key, code: 'workflow.review.record' },
    endpointSuffix: '/review/record',
    submitLabel: 'Record review',
    submissionHint: 'Records this decision for this visit. Complete the step when all requirements are met.',
    confirmation: 'Review recorded.',
    outcomePrompt: 'Choose a review outcome to record.',
  }],
};

/** Record v2 owns its distinct closed input contract; existing review commands remain v1. */
export const workflowReviewLookupCommandModule: WorkflowClientCommandModule = {
  code: 'platform.review.lookup', version: 1, commands: [{
    key: { provider: 'workflow', kind: 'command', code: 'workflow.review.record', version: 2 },
    form: WorkflowReviewRecordRequestForm,
    endpointSuffix: '/review/record/v2',
    submitLabel: 'Record review',
    submissionHint: 'Records this decision for this visit. Complete the step when all requirements are met.',
    confirmation: 'Review recorded.',
    outcomeField: 'outcome',
    outcomePrompt: 'Choose a review outcome to record.',
    supportsProvidedLookups: true,
    bindContext: submitReview.bindContext,
    validateInput: submitReview.validateInput,
    formForAction: action => {
      const allowed = new Set<string>(WorkflowReviewRecordRequestForm.fields.find(field => field.name === 'outcome')?.options.map(option => option.value));
      if (!Array.isArray(action.outcomes) || !action.outcomes.length
        || action.outcomes.some(outcome => !outcome || !allowed.has(outcome.code)
          || typeof outcome.label !== 'string' || !outcome.label.trim() || !['optional', 'required'].includes(outcome.reasonPolicy)
          || outcome.enabled !== undefined && typeof outcome.enabled !== 'boolean')
        || new Set(action.outcomes.map(outcome => outcome.code)).size !== action.outcomes.length) return null;
      return { ...WorkflowReviewRecordRequestForm, fields: WorkflowReviewRecordRequestForm.fields.map(field => field.name === 'outcome'
        ? { ...field, options: action.outcomes.map(outcome => ({ value: outcome.code, label: outcome.label, labelToken: '', ...(outcome.enabled === false ? { disabled: true } : {}) })) } : field) };
    },
    draftContextKey: action => {
      const binding = action.form?.configuration?.fields.find(field => field.name === 'notes')?.lookup;
      return binding ? JSON.stringify(['provided', WorkflowReviewRecordRequestForm.contract, 'notes', workflowFormLookupBindingKey(binding)]) : null;
    },
    serializeInput: (form, values, action) => {
      if (!action?.form || Object.keys(validateGeneratedForm(form, values)).length || Object.keys(submitReview.validateInput?.(action, values) ?? {}).length) return null;
      const resolved = resolveProvidedLookupFields(form, action.form.configuration, action.form.lookupFields);
      const revisions = resolved ? providedLookupRevisions(form, action.form.configuration, resolved.revisions, values) : null;
      if (!revisions || Object.keys(revisions).some(name => name !== 'notes')) return null;
      const input = editableFormPayload(form, values);
      return { ...input, ...(revisions['notes'] ? { notesLookupRevision: revisions['notes'] } : {}) };
    },
  }],
};
